import ChatbotVoiceSettings, { readConversationSettings, validConversationSettings } from './ChatbotVoiceSettings';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Typography,
    Paper,
    Breadcrumbs,
    Link,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Snackbar,
    Switch,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import chatbotService from '../services/ChatbotService';
import adminAgentService from '../services/AdminAgentService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeLaunchType(value) {
    const raw = String(value || '').trim();
    const lowered = raw.toLowerCase();
    if (lowered === 'widget') return 'Widget';
    if (lowered === 'popover') return 'Popover';
    if (lowered === 'embed' || lowered === 'ebed') return 'Embed';
    return 'Widget';
}

function normalizeFeedbackType(value) {
    const raw = String(value || '').trim();
    const lowered = raw.toLowerCase();
    if (!lowered || lowered === 'none') return 'None';
    if (lowered === 'thumbs' || lowered === 'thumb') return 'Thumbs';
    if (lowered === 'star' || lowered === 'stars') return 'Stars';
    return 'None';
}

function normalizeLocale(value) {
    const lowered = normalizeString(value).toLowerCase();
    if (lowered === 'ja' || lowered === 'jp' || lowered === 'ja-jp') return 'ja';
    if (lowered === 'en' || lowered === 'en-us') return 'en';
    return 'ja';
}

function normalizeMultilineList(value) {
    return String(value ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

const SYSTEM_ALLOWED_DOMAIN_HOSTS = new Set([
    'netbot.jp',
    'www.netbot.jp',
    'dashboard.netbot.jp',
]);

function normalizeDomainHost(value) {
    const raw = normalizeString(value).replace(/\/+$/, '');
    if (!raw) return '';
    try {
        const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
        return String(parsed.hostname || '').toLowerCase();
    } catch (e) {
        return raw.split('/')[0].split(':')[0].toLowerCase();
    }
}

function filterUserManagedDomains(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => normalizeString(line)).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.filter((line) => !SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

function includesSystemAllowedDomain(value) {
    const lines = Array.isArray(value)
        ? value.map((line) => normalizeString(line)).filter(Boolean)
        : normalizeMultilineList(value);
    return lines.some((line) => SYSTEM_ALLOWED_DOMAIN_HOSTS.has(normalizeDomainHost(line)));
}

const CHATBOT_VENDORS = [
    { value: 'VoiceFlow', label: 'VoiceFlow' },
    { value: 'NetBot', label: 'NetBot' },
];

function ChatbotDialog({ open, mode, initialChatbot, clientId, onClose, onSubmit, submitting }) {
    const { t } = useTranslation();
    const [vendor, setVendor] = useState('');
    const [projectId, setProjectId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [agentId, setAgentId] = useState('');
    const [chatbotName, setChatbotName] = useState('');
    const [headerImageUrl, setHeaderImageUrl] = useState('');
    const [avatarImageUrl, setAvatarImageUrl] = useState('');
    const [centerImageUrl, setCenterImageUrl] = useState('');
    const [header, setHeader] = useState('');
    const [bubbleTextLine1, setBubbleTextLine1] = useState('');
    const [bubbleTextLine2, setBubbleTextLine2] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [locale, setLocale] = useState('ja');
    const [footerText, setFooterText] = useState('');
    const [footerUrl, setFooterUrl] = useState('');
    const [hidePoweredBy, setHidePoweredBy] = useState(false);
    const [enableLiveAgent, setEnableLiveAgent] = useState(false);
    const [placeholder, setPlaceholder] = useState('');
    const [launchType, setLaunchType] = useState('Widget');
    const [conversationSettings, setConversationSettings] = useState(() => readConversationSettings());
    const [feedbackType, setFeedbackType] = useState('None');
    const [suggestionsText, setSuggestionsText] = useState('');
    const [domainsText, setDomainsText] = useState('');
    const [domainNotice, setDomainNotice] = useState('');

    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState('');

    useEffect(() => {
        if (!open) return;
        const bot = initialChatbot || {};
        setVendor(normalizeString(bot.vendor));
        setProjectId(normalizeString(bot.projectId));
        setApiKey(normalizeString(bot.apiKey));
        setAgentId(normalizeString(bot.agentId));
        setChatbotName(normalizeString(bot.chatbotName || bot.name));
        setHeaderImageUrl(normalizeString(bot.headerImageUrl || bot.imageUrl));
        setAvatarImageUrl(normalizeString(bot.avatarImageUrl || bot.imageUrl));
        setCenterImageUrl(normalizeString(bot.centerImageUrl || bot.imageUrl));
        setHeader(normalizeString(bot.header));
        setBubbleTextLine1(normalizeString(bot.bubbleTextLine1));
        setBubbleTextLine2(normalizeString(bot.bubbleTextLine2));
        setWelcomeMessage(normalizeString(bot.welcomeMessage));
        setLocale(normalizeLocale(bot.locale));
        setFooterText(normalizeString(bot.footerText));
        setFooterUrl(normalizeString(bot.footerUrl));
        setHidePoweredBy(Boolean(bot.hidePoweredBy));
        setEnableLiveAgent(Boolean(bot.enableLiveAgent));
        setPlaceholder(normalizeString(bot.placeholder));
        setLaunchType(normalizeLaunchType(bot.launchType));
        setConversationSettings(readConversationSettings(bot));
        setFeedbackType(normalizeFeedbackType(bot.feedbackType));
        if (Array.isArray(bot.initSuggestions)) {
            setSuggestionsText(bot.initSuggestions.filter(Boolean).join('\n'));
        } else {
            setSuggestionsText('');
        }
        if (Array.isArray(bot.domains)) {
            setDomainsText(filterUserManagedDomains(bot.domains).join('\n'));
        } else {
            setDomainsText('');
        }
        setDomainNotice('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialChatbot?.chatbotId]);

    useEffect(() => {
        const shouldLoad = open && normalizeString(vendor) === 'NetBot' && Boolean(clientId);
        if (!shouldLoad) return;

        let cancelled = false;
        const loadAgents = async () => {
            try {
                setAgentsError('');
                setAgentsLoading(true);
                const list = await adminAgentService.getAgents(clientId);
                if (cancelled) return;
                setAgents(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error('Failed to load agents for chatbot', e);
                if (!cancelled) {
                    setAgents([]);
                    setAgentsError(t('adminChatbots.agentsLoadError', 'Failed to load agents'));
                }
            } finally {
                if (!cancelled) setAgentsLoading(false);
            }
        };

        loadAgents();
        return () => {
            cancelled = true;
        };
    }, [open, vendor, clientId]);

    const payload = useMemo(() => {
        const lines = normalizeMultilineList(suggestionsText);
        const domains = filterUserManagedDomains(domainsText);

        const normalizedVendor = normalizeString(vendor);
        const isNetBot = normalizedVendor === 'NetBot';

        const body = {
            vendor: normalizedVendor || undefined,
            projectId: !isNetBot ? (projectId || undefined) : undefined,
            apiKey: !isNetBot ? (apiKey || undefined) : undefined,
            agentId: isNetBot ? (agentId || undefined) : undefined,
            chatbotName: chatbotName || undefined,
            headerImageUrl: headerImageUrl || undefined,
            avatarImageUrl: avatarImageUrl || undefined,
            centerImageUrl: centerImageUrl || undefined,
            header: header || undefined,
            bubbleTextLine1: bubbleTextLine1 || undefined,
            bubbleTextLine2: bubbleTextLine2 || undefined,
            welcomeMessage: welcomeMessage || undefined,
            locale: normalizeLocale(locale),
            footerText: footerText || undefined,
            footerUrl: footerUrl || undefined,
            hidePoweredBy: Boolean(hidePoweredBy),
            enableLiveAgent: Boolean(enableLiveAgent),
            placeholder: placeholder || undefined,
            launchType: normalizeLaunchType(launchType) || undefined,
            ...conversationSettings,
            feedbackType: normalizeFeedbackType(feedbackType) || undefined,
            initSuggestions: lines,
            domains,
        };
        return { body, lines, domains };
    }, [
        vendor,
        projectId,
        apiKey,
        agentId,
        chatbotName,
        headerImageUrl,
        avatarImageUrl,
        centerImageUrl,
        header,
        bubbleTextLine1,
        bubbleTextLine2,
        welcomeMessage,
        locale,
        footerText,
        footerUrl,
        hidePoweredBy,
        enableLiveAgent,
        placeholder,
        launchType,
        conversationSettings,
        feedbackType,
        suggestionsText,
        domainsText,
    ]);

    const canSubmit = !validConversationSettings(conversationSettings) ? false : mode === 'edit'
        ? true
        : Boolean(
            normalizeString(vendor) ||
                normalizeString(projectId) ||
                normalizeString(apiKey) ||
                normalizeString(agentId) ||
                normalizeString(chatbotName) ||
                normalizeString(header) ||
                normalizeString(bubbleTextLine1) ||
                normalizeString(bubbleTextLine2) ||
                normalizeString(welcomeMessage) ||
                normalizeLocale(locale) !== 'ja' ||
                normalizeString(footerText) ||
                normalizeString(footerUrl) ||
                hidePoweredBy === true ||
                enableLiveAgent === true ||
                normalizeString(placeholder) ||
                (payload.domains && payload.domains.length > 0) ||
                (payload.lines && payload.lines.length > 0) ||
                normalizeLaunchType(launchType) !== 'Widget' ||
                normalizeFeedbackType(feedbackType) !== 'None'
        );

    const title = mode === 'edit'
        ? (initialChatbot?.chatbotId
            ? t('adminChatbots.editTitleWithId', 'Edit Chatbot: {{id}}', { id: initialChatbot.chatbotId })
            : t('adminChatbots.editTitle', 'Edit Chatbot'))
        : t('adminChatbots.new', 'New Chatbot');

    const handleSubmit = async () => {
        await onSubmit(payload.body);
    };

    const handleDomainsTextChange = (value) => {
        setDomainsText(value);
        setDomainNotice(includesSystemAllowedDomain(value)
            ? t('adminChatbots.domainSystemNotice', 'That domain is already in the system default CORS list.')
            : '');
    };

    const agentIdExistsInList = useMemo(() => {
        const current = normalizeString(agentId);
        if (!current) return true;
        return (Array.isArray(agents) ? agents : []).some((a) => normalizeString(a?.agentId) === current);
    }, [agents, agentId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ pt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                    label={t('adminChatbots.chatbotName', 'Name')}
                    fullWidth
                    size="small"
                    value={chatbotName}
                    onChange={(e) => setChatbotName(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.vendor', 'Vendor')}
                    select
                    fullWidth
                    size="small"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    disabled={submitting}
                >
                    {(() => {
                        const current = normalizeString(vendor);
                        const exists = CHATBOT_VENDORS.some((v) => v.value === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{t('adminChatbots.currentOption', 'Current: {{value}}', { value: current })}</MenuItem>;
                        }
                        return null;
                    })()}
                    {CHATBOT_VENDORS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                </TextField>

                {normalizeString(vendor) === 'NetBot' ? (
                    <TextField
                        label={t('adminChatbots.agent', 'Agent')}
                        select
                        fullWidth
                        size="small"
                        value={agentId}
                        onChange={(e) => setAgentId(e.target.value)}
                        disabled={submitting}
                        helperText={agentsLoading
                            ? t('adminChatbots.loadingAgents', 'Loading agents…')
                            : (agentsError || (agents?.length ? '' : t('adminChatbots.noAgentsFound', 'No agents found')))}
                    >
                        <MenuItem value="">{t('adminChatbots.selectAgent', 'Select agent')}</MenuItem>
                        {normalizeString(agentId) && !agentIdExistsInList ? (
                            <MenuItem value={normalizeString(agentId)}>{t('adminChatbots.currentOption', 'Current: {{value}}', { value: normalizeString(agentId) })}</MenuItem>
                        ) : null}
                        {(Array.isArray(agents) ? agents : []).map((a) => {
                            const id = normalizeString(a?.agentId);
                            if (!id) return null;
                            const label = normalizeString(a?.agentName) || id;
                            return (
                                <MenuItem key={id} value={id}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                ) : (
                    <>
                        <TextField
                            label={t('adminChatbots.projectId', 'Project ID')}
                            fullWidth
                            size="small"
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            disabled={submitting}
                        />
                        <TextField
                            label={t('adminChatbots.apiKey', 'API Key')}
                            fullWidth
                            size="small"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            disabled={submitting}
                        />
                    </>
                )}

                <TextField
                    label={t('adminChatbots.header', 'Header')}
                    fullWidth
                    size="small"
                    value={header}
                    onChange={(e) => setHeader(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.welcomeMessage', 'Welcome Message')}
                    fullWidth
                    size="small"
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.locale', 'Locale')}
                    select
                    fullWidth
                    size="small"
                    value={normalizeLocale(locale)}
                    onChange={(e) => setLocale(e.target.value)}
                    disabled={submitting}
                >
                    <MenuItem value="en">{t('adminChatbots.localeEnglish', 'English')}</MenuItem>
                    <MenuItem value="ja">{t('adminChatbots.localeJapanese', 'Japanese')}</MenuItem>
                </TextField>
                <TextField
                    label={t('adminChatbots.bubbleLine1', 'Minimized Bubble Line 1')}
                    fullWidth
                    size="small"
                    value={bubbleTextLine1}
                    onChange={(e) => setBubbleTextLine1(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.bubbleLine2', 'Minimized Bubble Line 2')}
                    fullWidth
                    size="small"
                    value={bubbleTextLine2}
                    onChange={(e) => setBubbleTextLine2(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.footerText', 'Footer Text')}
                    fullWidth
                    size="small"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.footerUrl', 'Footer URL')}
                    fullWidth
                    size="small"
                    value={footerUrl}
                    onChange={(e) => setFooterUrl(e.target.value)}
                    disabled={submitting}
                />
                <TextField
                    label={t('adminChatbots.placeholder', 'Placeholder')}
                    fullWidth
                    size="small"
                    value={placeholder}
                    onChange={(e) => setPlaceholder(e.target.value)}
                    disabled={submitting}
                />
                <ChatbotVoiceSettings value={conversationSettings} onChange={setConversationSettings}
                    disabled={submitting} voiceAllowed={vendor === 'NetBot'} />
                <TextField
                    label={t('adminChatbots.launchType', 'Launch Type')}
                    select
                    fullWidth
                    size="small"
                    value={launchType}
                    onChange={(e) => setLaunchType(e.target.value)}
                    disabled={submitting}
                >
                    <MenuItem value="Widget">{t('adminChatbots.launchTypeWidget', 'Widget')}</MenuItem>
                    <MenuItem value="Popover">{t('adminChatbots.launchTypePopover', 'Popover')}</MenuItem>
                    <MenuItem value="Embed">{t('adminChatbots.launchTypeEmbed', 'Embed')}</MenuItem>
                </TextField>
                <TextField
                    label={t('adminChatbots.feedbackType', 'Feedback Type')}
                    select
                    fullWidth
                    size="small"
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    disabled={submitting}
                >
                    <MenuItem value="None">{t('adminChatbots.feedbackTypeNone', 'None')}</MenuItem>
                    <MenuItem value="Thumbs">{t('adminChatbots.feedbackTypeThumbs', 'Thumbs')}</MenuItem>
                    <MenuItem value="Stars">{t('adminChatbots.feedbackTypeStars', 'Stars')}</MenuItem>
                </TextField>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                        control={<Switch checked={hidePoweredBy} onChange={(e) => setHidePoweredBy(e.target.checked)} />}
                        label={t('adminChatbots.hidePoweredBy', 'Hide Powered By')}
                        disabled={submitting}
                    />
                    <FormControlLabel
                        control={<Switch checked={enableLiveAgent} onChange={(e) => setEnableLiveAgent(e.target.checked)} />}
                        label={t('adminChatbots.enableLiveAgent', 'Enable Live Agent')}
                        disabled={submitting}
                    />
                </Box>
                <TextField
                    label={t('adminChatbots.initSuggestions', 'Init Suggestions (one per line)')}
                    fullWidth
                    multiline
                    minRows={4}
                    value={suggestionsText}
                    onChange={(e) => setSuggestionsText(e.target.value)}
                    disabled={submitting}
                    sx={{ gridColumn: '1 / -1' }}
                />
                <TextField
                    label={t('adminChatbots.allowedDomains', 'Allowed Domains (one per line)')}
                    fullWidth
                    multiline
                    minRows={4}
                    value={domainsText}
                    onChange={(e) => handleDomainsTextChange(e.target.value)}
                    disabled={submitting}
                    helperText={domainNotice || t('adminChatbots.domainsHelper', 'NetBot domains are allowed by the system. Add only customer domains here.')}
                    sx={{ gridColumn: '1 / -1' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    {t('adminChatbots.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={submitting || !canSubmit}
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                >
                    {mode === 'edit'
                        ? (submitting ? t('adminChatbots.saving', 'Saving…') : t('adminChatbots.save', 'Save'))
                        : (submitting ? t('adminChatbots.creating', 'Creating…') : t('adminChatbots.create', 'Create'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ConfirmDeleteDialog({ open, chatbot, onClose, onConfirm, submitting }) {
    const { t } = useTranslation();
    const name = normalizeString(chatbot?.chatbotName) || normalizeString(chatbot?.chatbotId) || t('adminChatbots.chatbotFallbackName', 'Chatbot');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{t('adminChatbots.deleteTitle', 'Delete Chatbot')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    <Trans
                        i18nKey="adminChatbots.deleteConfirm"
                        values={{ name }}
                        defaults="Are you sure you want to delete <strong>{{name}}</strong>?"
                        components={{ strong: <strong /> }}
                    />
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    {t('adminChatbots.cancel', 'Cancel')}
                </Button>
                <Button color="error" variant="contained" onClick={onConfirm} disabled={submitting || !chatbot?.chatbotId} sx={{ textTransform: 'none' }}>
                    {t('adminChatbots.delete', 'Delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function AdminChatbots({ embedded = false }) {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [agents, setAgents] = useState([]);

    const agentNameById = useMemo(() => {
        const map = {};
        (Array.isArray(agents) ? agents : []).forEach((a) => {
            const id = normalizeString(a?.agentId);
            if (!id) return;
            map[id] = normalizeString(a?.agentName) || id;
        });
        return map;
    }, [agents]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogInitialChatbot, setDialogInitialChatbot] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingChatbot, setDeletingChatbot] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchChatbots = async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            setError('');
            const data = await chatbotService.getAdminChatbots(clientId);
            setChatbots(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching admin chatbots', e);
            setError(t('adminChatbots.fetchError', 'Failed to load admin chatbots'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) fetchChatbots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    useEffect(() => {
        if (!clientId) {
            setAgents([]);
            return;
        }

        let cancelled = false;
        const loadAgents = async () => {
            try {
                const list = await adminAgentService.getAgents(clientId);
                if (!cancelled) setAgents(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error('Error fetching admin agents for chatbots list', e);
                if (!cancelled) setAgents([]);
            }
        };

        loadAgents();
        return () => {
            cancelled = true;
        };
    }, [clientId]);

    const handleCloseError = () => setError('');
    const handleCloseSuccess = () => setSuccess('');

    const openCreate = () => {
        setDialogMode('create');
        setDialogInitialChatbot(null);
        setDialogOpen(true);
    };

    const openEdit = (bot) => {
        setDialogMode('edit');
        setDialogInitialChatbot(bot || null);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (submitting) return;
        setDialogOpen(false);
    };

    const openDelete = (bot) => {
        setDeletingChatbot(bot || null);
        setDeleteDialogOpen(true);
    };

    const closeDelete = () => {
        if (deleting) return;
        setDeleteDialogOpen(false);
        setDeletingChatbot(null);
    };

    const confirmDelete = async () => {
        if (!clientId) return;
        const chatbotId = deletingChatbot?.chatbotId;
        if (!chatbotId) return;

        try {
            setDeleting(true);
            setError('');
            await chatbotService.deleteAdminChatbot(clientId, chatbotId);
            setSuccess(t('adminChatbots.deleted', 'Chatbot deleted'));
            closeDelete();
            fetchChatbots();
        } catch (e) {
            console.error('Failed to delete admin chatbot', e);
            setError(e?.message || t('adminChatbots.deleteError', 'Failed to delete chatbot'));
        } finally {
            setDeleting(false);
        }
    };

    const submitDialog = async (payload) => {
        if (!clientId) return;

        try {
            setSubmitting(true);
            if (dialogMode === 'edit') {
                const id = dialogInitialChatbot?.chatbotId;
                if (!id) {
                    throw new Error('Missing chatbotId for edit');
                }
                await chatbotService.updateAdminChatbot(clientId, id, payload);
            } else {
                await chatbotService.createAdminChatbot(clientId, payload);
            }
            closeDialog();
            fetchChatbots();
        } catch (e) {
            console.error('Failed to save admin chatbot', e);
            setError(t('adminChatbots.fetchError', 'Failed to load admin chatbots'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: embedded ? 0 : 2 }}>
            {!embedded ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                            <Link
                                component={RouterLink}
                                underline="hover"
                                color="inherit"
                                to="/customer-list"
                                sx={{ fontSize: 13 }}
                            >
                                {t('adminChatbots.clientList', 'Client List')}
                            </Link>
                            <Typography color="text.primary" sx={{ fontSize: 13 }}>
                                {t('adminChatbots.breadcrumbChatbots', 'Chatbots')}
                            </Typography>
                        </Breadcrumbs>
                        <Typography variant="h5" fontWeight="bold">
                            {t('adminChatbots.title', 'Admin Chatbots')}{clientId ? `: ${clientId}` : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/customer-list')}
                            disabled={loading}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('adminChatbots.backToClients', 'Back to Clients')}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={openCreate}
                            disabled={loading || !clientId}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('adminChatbots.new', 'New Chatbot')}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={openCreate}
                        disabled={loading || !clientId}
                        sx={{ textTransform: 'none', borderRadius: 3 }}
                    >
                        {t('adminChatbots.new', 'New Chatbot')}
                    </Button>
                </Box>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.chatbotId', 'Chatbot ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.vendor', 'Vendor')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.agent', 'Agent')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.projectId', 'Project ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.chatbotName', 'Name')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminChatbots.actions', 'Actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {chatbots.map((bot) => (
                            <TableRow key={bot.chatbotId} hover>
                                <TableCell>{bot.chatbotId}</TableCell>
                                <TableCell>{bot.vendor || '-'}</TableCell>
                                <TableCell>
                                    {normalizeString(bot.vendor) === 'NetBot'
                                        ? (agentNameById[normalizeString(bot.agentId)] || bot.agentId || '-')
                                        : '-'}
                                </TableCell>
                                <TableCell>{bot.projectId || '-'}</TableCell>
                                <TableCell>{bot.chatbotName || '-'}</TableCell>
                                <TableCell>
                                    <Tooltip title={t('adminChatbots.edit', 'Edit')} placement="top" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openEdit(bot)}
                                            disabled={submitting || deleting}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title={t('adminChatbots.delete', 'Delete')} placement="top" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openDelete(bot)}
                                            disabled={submitting || deleting}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {chatbots.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {t('adminChatbots.empty', 'No chatbots found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error">
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={Boolean(success)}
                autoHideDuration={4000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSuccess} severity="success">
                    {success}
                </Alert>
            </Snackbar>

            <ChatbotDialog
                open={dialogOpen}
                mode={dialogMode}
                initialChatbot={dialogInitialChatbot}
                clientId={clientId}
                onClose={closeDialog}
                onSubmit={submitDialog}
                submitting={submitting}
            />

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                chatbot={deletingChatbot}
                onClose={closeDelete}
                onConfirm={confirmDelete}
                submitting={deleting}
            />
        </Box>
    );
}
